import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Edit, X, Loader2, Upload, FileDown, Monitor, Smartphone, Globe, ExternalLink, Eye, EyeOff, ImageIcon } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FileUploadZone = ({ label, accept, acceptLabel, currentPath, currentName, platform, onUploaded, onRemove, token, icon: Icon, color }) => {
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const fileRef = useRef(null);

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        setProgress(0);

        const formData = new FormData();
        formData.append('file', file);
        formData.append('platform', platform);

        try {
            const res = await axios.post(`${API}/upload-demo`, formData, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); },
            });
            onUploaded(res.data.path, file.name);
            toast.success(`${label} - Fichier upload\u00e9`);
        } catch (err) {
            toast.error(`Erreur upload ${label}`);
        } finally {
            setUploading(false);
            setProgress(0);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    return (
        <div className={`border rounded-lg p-4 ${currentPath ? `border-${color}-500/40 bg-${color}-500/5` : 'border-slate-600 bg-slate-800/50'}`}
             data-testid={`upload-${platform}`}>
            <div className="flex items-center gap-2 mb-3">
                <Icon size={18} className={currentPath ? `text-${color}-400` : 'text-slate-400'} />
                <span className="text-sm font-bold text-white">{label}</span>
                {currentPath && (
                    <span className="ml-auto px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-400">Upload\u00e9</span>
                )}
            </div>
            {currentPath ? (
                <div className="flex items-center gap-2">
                    <FileDown size={14} className="text-slate-400" />
                    <span className="text-xs text-slate-300 truncate flex-1" title={currentName}>{currentName}</span>
                    <button onClick={onRemove} className="text-red-400 hover:text-red-300 p-1" data-testid={`remove-${platform}`}>
                        <X size={14} />
                    </button>
                </div>
            ) : (
                <>
                    <input ref={fileRef} type="file" accept={accept} onChange={handleUpload} className="hidden" data-testid={`file-input-${platform}`} />
                    <button onClick={() => fileRef.current?.click()} disabled={uploading}
                        className="w-full border-2 border-dashed border-slate-600 rounded-lg py-3 px-4 text-center hover:border-slate-400 transition-colors cursor-pointer">
                        {uploading ? (
                            <div className="flex flex-col items-center gap-1">
                                <Loader2 size={18} className="animate-spin text-blue-400" />
                                <span className="text-xs text-slate-400">{progress}%</span>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center gap-1">
                                <Upload size={18} className="text-slate-500" />
                                <span className="text-xs text-slate-500">{acceptLabel}</span>
                            </div>
                        )}
                    </button>
                </>
            )}
        </div>
    );
};

const ImageUploadZone = ({ currentUrl, onUploaded, onRemove, token }) => {
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);
    const { t } = useLanguage();

    const fullUrl = currentUrl
        ? (currentUrl.startsWith('http') ? currentUrl : `${API.replace('/api', '')}${currentUrl.startsWith('/') ? '' : '/'}${currentUrl}`)
        : null;

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        try {
            const res = await axios.post(`${API}/upload-image`, formData, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
            });
            onUploaded(res.data.url);
            toast.success(t('Image upload\u00e9e', 'Image uploaded'));
        } catch (err) {
            toast.error(t('Erreur upload image', 'Image upload error'));
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    return (
        <div data-testid="upload-screenshot">
            <Label className="text-white font-bold mb-2 block text-sm">{t('Image de couverture', 'Cover image')}</Label>
            {currentUrl ? (
                <div className="relative rounded-lg overflow-hidden border border-slate-600 bg-slate-800">
                    <img src={fullUrl} alt="cover" className="w-full h-40 object-cover" onError={(e) => { e.target.style.display='none'; }} />
                    <div className="absolute top-2 right-2 flex gap-2">
                        <button onClick={() => fileRef.current?.click()}
                            className="p-1.5 rounded-full bg-slate-900/80 text-white hover:bg-blue-600 transition-colors" title={t('Changer', 'Change')}>
                            <ImageIcon size={14} />
                        </button>
                        <button onClick={onRemove}
                            className="p-1.5 rounded-full bg-slate-900/80 text-red-400 hover:bg-red-600 hover:text-white transition-colors" title={t('Supprimer', 'Remove')}>
                            <X size={14} />
                        </button>
                    </div>
                    <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
                </div>
            ) : (
                <>
                    <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="file-input-screenshot" />
                    <button onClick={() => fileRef.current?.click()} disabled={uploading}
                        className="w-full border-2 border-dashed border-slate-600 rounded-lg py-6 px-4 text-center hover:border-slate-400 transition-colors cursor-pointer">
                        {uploading ? (
                            <div className="flex flex-col items-center gap-2">
                                <Loader2 size={24} className="animate-spin text-blue-400" />
                                <span className="text-xs text-slate-400">{t('Upload en cours...', 'Uploading...')}</span>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center gap-2">
                                <ImageIcon size={24} className="text-slate-500" />
                                <span className="text-sm text-slate-400">{t('Cliquez pour ajouter une image', 'Click to add an image')}</span>
                                <span className="text-xs text-slate-500">JPG, PNG, WebP</span>
                            </div>
                        )}
                    </button>
                </>
            )}
        </div>
    );
};

const ApplicationsSection = () => {
    const { t } = useLanguage();
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingApp, setEditingApp] = useState(null);
    const [saving, setSaving] = useState(false);

    const emptyForm = {
        nom: '', description: '', description_en: '', type: 'web',
        technos: [], contexte: 'client', screenshot: '', lien: '',
        demontrable: false, demo_type: null,
        file_standalone: null, file_standalone_name: null,
        file_android: null, file_android_name: null,
        file_ios: null, file_ios_name: null,
    };
    const [formData, setFormData] = useState(emptyForm);
    const [validationErrors, setValidationErrors] = useState([]);

    const token = localStorage.getItem('dkr-token');
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

    useEffect(() => { fetchApps(); }, []);

    const fetchApps = async () => {
        try { const res = await axios.get(`${API}/applications`); setApps(res.data); }
        catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const openCreate = () => { setEditingApp(null); setFormData(emptyForm); setValidationErrors([]); setDialogOpen(true); };

    const openEdit = (app) => {
        setEditingApp(app);
        setFormData({
            nom: app.nom || '', description: app.description || '', description_en: app.description_en || '',
            type: app.type || 'web', technos: app.technos || [], contexte: app.contexte || 'client',
            screenshot: app.screenshot || '', lien: app.lien || '', demontrable: app.demontrable || false,
            demo_type: app.demo_type || null,
            file_standalone: app.file_standalone || null, file_standalone_name: app.file_standalone_name || null,
            file_android: app.file_android || null, file_android_name: app.file_android_name || null,
            file_ios: app.file_ios || null, file_ios_name: app.file_ios_name || null,
        });
        setValidationErrors([]);
        setDialogOpen(true);
    };

    const validate = () => {
        const errors = [];
        if (!formData.nom.trim()) errors.push(t('Le nom est requis', 'Name is required'));
        if (formData.demontrable && formData.demo_type === 'mobile') {
            if (formData.file_android && !formData.file_ios)
                errors.push(t('Si Android est renseign\u00e9, iOS doit aussi l\'\u00eatre', 'If Android is provided, iOS must also be provided'));
            if (formData.file_ios && !formData.file_android)
                errors.push(t('Si iOS est renseign\u00e9, Android doit aussi l\'\u00eatre', 'If iOS is provided, Android must also be provided'));
        }
        setValidationErrors(errors);
        return errors.length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;
        setSaving(true);

        // Clean fields based on demo_type
        const payload = { ...formData };
        payload.technos = Array.isArray(payload.technos) ? payload.technos : String(payload.technos).split(',').map(s => s.trim()).filter(Boolean);
        payload.demontrable = payload.demontrable === true || payload.demontrable === 'true';

        if (!payload.demontrable || payload.demo_type !== 'web') payload.lien = null;
        if (!payload.demontrable || payload.demo_type !== 'standalone') { payload.file_standalone = null; payload.file_standalone_name = null; }
        if (!payload.demontrable || payload.demo_type !== 'mobile') { payload.file_android = null; payload.file_android_name = null; payload.file_ios = null; payload.file_ios_name = null; }
        if (!payload.demontrable) payload.demo_type = null;

        try {
            if (editingApp) {
                await axios.put(`${API}/applications/${editingApp.id}`, payload, authHeaders);
                toast.success(t('Application mise \u00e0 jour', 'Application updated'));
            } else {
                await axios.post(`${API}/applications`, payload, authHeaders);
                toast.success(t('Application cr\u00e9\u00e9e', 'Application created'));
            }
            fetchApps();
            setDialogOpen(false);
        } catch (e) {
            toast.error(t('Erreur lors de la sauvegarde', 'Error saving'));
        } finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('Confirmer la suppression ?', 'Confirm deletion?'))) return;
        try {
            await axios.delete(`${API}/applications/${id}`, authHeaders);
            toast.success(t('Application supprim\u00e9e', 'Application deleted'));
            fetchApps();
        } catch (e) { toast.error(t('Erreur', 'Error')); }
    };

    const toggleVisibility = async (app) => {
        try {
            const res = await axios.put(`${API}/applications/${app.id}/toggle-visibility`, {}, authHeaders);
            const isVisible = res.data.visible;
            toast.success(isVisible ? t('Application visible', 'Application visible') : t('Application masqu\u00e9e', 'Application hidden'));
            fetchApps();
        } catch (e) { toast.error(t('Erreur', 'Error')); }
    };

    const setField = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));

    const getDemoLabel = (app) => {
        if (!app.demontrable) return null;
        if (app.demo_type === 'web' && app.lien) return { text: 'Web', color: 'violet', icon: Globe };
        if (app.demo_type === 'standalone' && app.file_standalone) return { text: 'JAR', color: 'cyan', icon: Monitor };
        if (app.demo_type === 'mobile' && (app.file_android || app.file_ios)) return { text: 'Mobile', color: 'green', icon: Smartphone };
        return null;
    };

    if (loading) return <div className="flex justify-center py-12"><div className="spinner"></div></div>;

    return (
        <div data-testid="admin-applications">
            <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-bold text-white">Applications</h2>
                <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-500" data-testid="add-app-btn">
                    <Plus className="w-4 h-4 mr-2" />{t('Ajouter', 'Add')}
                </Button>
            </div>

            <div className="space-y-4">
                {apps.length === 0 ? (
                    <div className="glass-card p-12 text-center"><p className="text-slate-400">{t('Aucune application', 'No applications')}</p></div>
                ) : apps.map(app => {
                    const demo = getDemoLabel(app);
                    return (
                        <div key={app.id} className={`glass-card p-4 flex items-center justify-between ${app.visible === false ? 'opacity-50' : ''}`} data-testid={`app-item-${app.id}`}>
                            <div className="flex-1 flex items-center flex-wrap gap-2">
                                <span className="text-white font-medium">{app.nom}</span>
                                <span className={`px-2 py-0.5 text-xs rounded-full ${
                                    app.type === 'web' ? 'bg-blue-500/20 text-blue-400' :
                                    app.type === 'mobile' ? 'bg-green-500/20 text-green-400' :
                                    'bg-slate-500/20 text-slate-400'
                                }`}>{app.type}</span>
                                {demo && (
                                    <span className={`px-2 py-0.5 text-xs rounded-full bg-${demo.color}-500/20 text-${demo.color}-400 flex items-center gap-1`}>
                                        <demo.icon size={10} />{t('D\u00e9mo', 'Demo')} {demo.text}
                                    </span>
                                )}
                                {app.visible === false && (
                                    <span className="px-2 py-0.5 text-xs rounded-full bg-red-500/20 text-red-400">{t('Masqu\u00e9', 'Hidden')}</span>
                                )}
                            </div>
                            <div className="flex gap-1">
                                <Button size="sm" variant="ghost"
                                    onClick={() => toggleVisibility(app)}
                                    className={app.visible === false ? 'text-red-400 hover:text-green-300' : 'text-green-400 hover:text-red-300'}
                                    title={app.visible === false ? t('Afficher', 'Show') : t('Masquer', 'Hide')}
                                    data-testid={`toggle-vis-${app.id}`}>
                                    {app.visible === false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => openEdit(app)} data-testid={`edit-app-${app.id}`}><Edit className="w-4 h-4" /></Button>
                                <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => handleDelete(app.id)} data-testid={`delete-app-${app.id}`}><Trash2 className="w-4 h-4" /></Button>
                            </div>
                        </div>
                    );
                })}
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-slate-900 border-white/20 max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-white text-xl font-bold">
                            {editingApp ? t('Modifier l\'application', 'Edit application') : t('Nouvelle application', 'New application')}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-5 mt-4">
                        {validationErrors.length > 0 && (
                            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3" data-testid="validation-errors">
                                {validationErrors.map((err, i) => <p key={i} className="text-red-400 text-sm">{err}</p>)}
                            </div>
                        )}

                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-white font-bold mb-2 block text-sm">Nom *</Label>
                                <Input value={formData.nom} onChange={e => setField('nom', e.target.value)}
                                    className="bg-slate-700 border-slate-500 text-white font-medium" data-testid="app-nom" />
                            </div>
                            <div>
                                <Label className="text-white font-bold mb-2 block text-sm">Type</Label>
                                <Select value={formData.type} onValueChange={v => setField('type', v)}>
                                    <SelectTrigger className="bg-slate-700 border-slate-500 text-white font-medium"><SelectValue /></SelectTrigger>
                                    <SelectContent className="bg-slate-700 border-slate-500">
                                        <SelectItem value="web" className="text-white font-medium">Web</SelectItem>
                                        <SelectItem value="mobile" className="text-white font-medium">Mobile</SelectItem>
                                        <SelectItem value="standalone" className="text-white font-medium">Standalone</SelectItem>
                                        <SelectItem value="outil" className="text-white font-medium">{t('Outil', 'Tool')}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div>
                            <Label className="text-white font-bold mb-2 block text-sm">Description (FR)</Label>
                            <Textarea value={formData.description} onChange={e => setField('description', e.target.value)}
                                className="bg-slate-700 border-slate-500 text-white font-medium" rows={3} data-testid="app-description" />
                        </div>
                        <div>
                            <Label className="text-white font-bold mb-2 block text-sm">Description (EN)</Label>
                            <Textarea value={formData.description_en} onChange={e => setField('description_en', e.target.value)}
                                className="bg-slate-700 border-slate-500 text-white font-medium" rows={3} />
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-white font-bold mb-2 block text-sm">Technologies</Label>
                                <Input value={Array.isArray(formData.technos) ? formData.technos.join(', ') : formData.technos}
                                    onChange={e => setField('technos', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                                    className="bg-slate-700 border-slate-500 text-white font-medium"
                                    placeholder={t('S\u00e9par\u00e9s par des virgules', 'Comma separated')} />
                            </div>
                            <div>
                                <Label className="text-white font-bold mb-2 block text-sm">Contexte</Label>
                                <Select value={formData.contexte} onValueChange={v => setField('contexte', v)}>
                                    <SelectTrigger className="bg-slate-700 border-slate-500 text-white font-medium"><SelectValue /></SelectTrigger>
                                    <SelectContent className="bg-slate-700 border-slate-500">
                                        <SelectItem value="client" className="text-white font-medium">Client</SelectItem>
                                        <SelectItem value="personnel" className="text-white font-medium">{t('Personnel', 'Personal')}</SelectItem>
                                        <SelectItem value="prestation" className="text-white font-medium">Prestation</SelectItem>
                                        <SelectItem value="professionnel" className="text-white font-medium">{t('Professionnel', 'Professional')}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <ImageUploadZone
                            currentUrl={formData.screenshot}
                            onUploaded={(url) => setField('screenshot', url)}
                            onRemove={() => setField('screenshot', '')}
                            token={token}
                        />

                        {/* Demo section */}
                        <div className="border-t border-white/10 pt-5">
                            <h3 className="text-lg font-bold text-white mb-4">{t('D\u00e9mo', 'Demo')}</h3>

                            <div className="mb-4">
                                <Label className="text-white font-bold mb-2 block text-sm">{t('D\u00e9mo disponible', 'Demo available')}</Label>
                                <Select value={formData.demontrable ? 'true' : 'false'} onValueChange={v => setField('demontrable', v === 'true')}>
                                    <SelectTrigger className="bg-slate-700 border-slate-500 text-white font-medium w-40"><SelectValue /></SelectTrigger>
                                    <SelectContent className="bg-slate-700 border-slate-500">
                                        <SelectItem value="true" className="text-white font-medium">{t('Oui', 'Yes')}</SelectItem>
                                        <SelectItem value="false" className="text-white font-medium">{t('Non', 'No')}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {formData.demontrable && (
                                <>
                                    <div className="mb-5">
                                        <Label className="text-white font-bold mb-2 block text-sm">{t('Type de d\u00e9mo', 'Demo type')}</Label>
                                        <Select value={formData.demo_type || ''} onValueChange={v => setField('demo_type', v)}>
                                            <SelectTrigger className="bg-slate-700 border-slate-500 text-white font-medium" data-testid="demo-type-select">
                                                <SelectValue placeholder={t('Choisir le type...', 'Choose type...')} />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-700 border-slate-500">
                                                <SelectItem value="web" className="text-white font-medium">
                                                    <span className="flex items-center gap-2"><Globe size={14} />{t('Application Web (URL)', 'Web App (URL)')}</span>
                                                </SelectItem>
                                                <SelectItem value="standalone" className="text-white font-medium">
                                                    <span className="flex items-center gap-2"><Monitor size={14} />{t('Application Standalone (JAR)', 'Standalone App (JAR)')}</span>
                                                </SelectItem>
                                                <SelectItem value="mobile" className="text-white font-medium">
                                                    <span className="flex items-center gap-2"><Smartphone size={14} />{t('Application Mobile (APK / IPA)', 'Mobile App (APK / IPA)')}</span>
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {/* Web: URL input */}
                                    {formData.demo_type === 'web' && (
                                        <div>
                                            <Label className="text-white font-bold mb-2 block text-sm">{t('URL de la d\u00e9mo', 'Demo URL')}</Label>
                                            <Input value={formData.lien || ''} onChange={e => setField('lien', e.target.value)}
                                                className="bg-slate-700 border-slate-500 text-white font-medium"
                                                placeholder="https://..." data-testid="app-lien" />
                                        </div>
                                    )}

                                    {/* Standalone: JAR upload */}
                                    {formData.demo_type === 'standalone' && (
                                        <FileUploadZone
                                            label={t('Fichier JAR', 'JAR File')}
                                            accept=".jar"
                                            acceptLabel=".jar"
                                            platform="standalone"
                                            currentPath={formData.file_standalone}
                                            currentName={formData.file_standalone_name}
                                            onUploaded={(path, name) => setFormData(prev => ({ ...prev, file_standalone: path, file_standalone_name: name }))}
                                            onRemove={() => setFormData(prev => ({ ...prev, file_standalone: null, file_standalone_name: null }))}
                                            token={token}
                                            icon={Monitor}
                                            color="cyan"
                                        />
                                    )}

                                    {/* Mobile: APK + IPA uploads */}
                                    {formData.demo_type === 'mobile' && (
                                        <div className="grid md:grid-cols-2 gap-4">
                                            <FileUploadZone
                                                label="Android (APK)"
                                                accept=".apk,.aab"
                                                acceptLabel=".apk / .aab"
                                                platform="android"
                                                currentPath={formData.file_android}
                                                currentName={formData.file_android_name}
                                                onUploaded={(path, name) => setFormData(prev => ({ ...prev, file_android: path, file_android_name: name }))}
                                                onRemove={() => setFormData(prev => ({ ...prev, file_android: null, file_android_name: null }))}
                                                token={token}
                                                icon={Smartphone}
                                                color="green"
                                            />
                                            <FileUploadZone
                                                label="iOS (IPA)"
                                                accept=".ipa"
                                                acceptLabel=".ipa"
                                                platform="ios"
                                                currentPath={formData.file_ios}
                                                currentName={formData.file_ios_name}
                                                onUploaded={(path, name) => setFormData(prev => ({ ...prev, file_ios: path, file_ios_name: name }))}
                                                onRemove={() => setFormData(prev => ({ ...prev, file_ios: null, file_ios_name: null }))}
                                                token={token}
                                                icon={Smartphone}
                                                color="violet"
                                            />
                                            <p className="text-xs text-slate-400 md:col-span-2">
                                                {t('Android et iOS doivent \u00eatre renseign\u00e9s ensemble.', 'Android and iOS must be provided together.')}
                                            </p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('Annuler', 'Cancel')}</Button>
                            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-500" data-testid="save-app-btn">
                                {saving && <Loader2 className="animate-spin mr-2 w-4 h-4" />}
                                {editingApp ? t('Mettre \u00e0 jour', 'Update') : t('Cr\u00e9er', 'Create')}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ApplicationsSection;
